import React, { useContext, useEffect, useState } from 'react';
import { Row, Col, Button, Card, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { SubscriptionService } from './service';
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import BasicModal from '../../Common/BasicModal';
import { EnumsContext } from '../../context/EnumsContext';

interface SubscriptionsTabProps {
  userId: number;
}

const SubscriptionsTab: React.FC<SubscriptionsTabProps> = ({ userId }) => {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [duration, setDuration] = useState<string>('month');
  const [showModal, setShowModal] = useState(false);
  // Which subscription action is pending confirmation (null = no modal open)
  const [pendingAction, setPendingAction] = useState<
    'cancelAtPeriodEnd' | 'renewSubscription' | 'cancelImmediately' | 'cleanup' | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showChangePackageModal, setShowChangePackageModal] = useState(false);
  const { enums } = useContext(EnumsContext); // Access enums from context
  const subscriptionDuration = enums.subscriptionDuration; // Get subscriptionDuration array

  const fetchSubscriptions = async () => {
    try {
      const response = await SubscriptionService.getAll(userId);
      if (response && response.subscription && response.subscription.isActive) {
        setCurrentSubscription(response.subscription);
        setSubscriptions([response.subscription]);
      } else {
        setCurrentSubscription(null);
        setSubscriptions([]);
      }
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchSubscriptionHistory = async () => {
    try {
      const response = await SubscriptionService.getSubscriptionHistory(userId);
      setSubscriptionHistory(response);
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchAvailablePackages = async () => {
    try {
      const response = await SubscriptionService.getAvailablePackages();
      const mappedPackages = response.map((pkg: any) => ({
        label: pkg.name,
        value: pkg.id,
      }));
      setPackages(mappedPackages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchAllData = async () => {
    await Promise.all([fetchSubscriptions(), fetchSubscriptionHistory(), fetchAvailablePackages()]);
  };

  useEffect(() => {
    fetchAllData();
  }, [userId]);

  const changePackage = async () => {
    try {
      const response = await SubscriptionService.getStripeUrl(selectedPackage!, {
        userId,
        subscriptionPackage: selectedPackage!,
        subscriptionDuration: duration,
        successUrl: window.location.href,
        cancelUrl: window.location.href,
      });
      if (response.url) {
        window.open(response.url, '_blank');
        setShowChangePackageModal(false);
        setSelectedPackage(null);
        setDuration('month');
        await fetchAllData();
      }
    } catch (error) {
      ErrorToast(error);
    }
  };

  // Metadata for the shared confirmation modal, keyed by the pending action.
  const actionConfig = {
    cancelAtPeriodEnd: {
      title: t('user.confirmCancellation'),
      body: t('user.confirmCancellationText'),
      confirmLabel: t('user.cancelSubscriptionAction'),
      confirmVariant: 'danger',
      success: t('user.subscriptionCancelled'),
      request: () => SubscriptionService.cancelAtPeriodEnd(userId),
    },
    renewSubscription: {
      title: t('user.confirmRenewal'),
      body: t('user.confirmRenewalText'),
      confirmLabel: t('user.renewSubscription'),
      confirmVariant: 'success',
      success: t('user.subscriptionRenewed'),
      request: () => SubscriptionService.renewSubscription(userId),
    },
    cancelImmediately: {
      title: t('user.confirmCancelImmediately'),
      body: t('user.confirmCancelImmediatelyText'),
      confirmLabel: t('user.cancelImmediately'),
      confirmVariant: 'danger',
      success: t('user.subscriptionCancelledImmediately'),
      request: () => SubscriptionService.cancelImmediately(userId),
    },
    cleanup: {
      title: t('user.confirmCleanup'),
      body: t('user.confirmCleanupText'),
      confirmLabel: t('user.cleanup'),
      confirmVariant: 'warning',
      success: t('user.cleanupSuccess'),
      request: () => SubscriptionService.cleanup(userId),
    },
  } as const;

  const confirmAction = async () => {
    if (!pendingAction) return;
    const config = actionConfig[pendingAction];
    try {
      setActionLoading(true);
      await config.request();
      SuccessToast(config.success);
      
      const action = pendingAction;
      setPendingAction(null);

      if (action === 'cancelImmediately' || action === 'cleanup') {
        setCurrentSubscription(null);
        setSubscriptions([]);
        setTimeout(async () => {
          await fetchAllData();
        }, 2000);
      } else {
        await fetchAllData();
      }
    } catch (error) {
      ErrorToast(error);
    } finally {
      setActionLoading(false);
    }
  };

  if (!subscriptionHistory.length && !currentSubscription && !packages.length) {
    return <div>{t('user.loading')}</div>;
  }

  return (
    <Row>
      {/* Current Subscription */}
      <Col sm={3}>
        <Card className="shadow-none border my-4">
          <Card.Body>
            <h6 className="mb-3 fw-medium">{t('user.currentSubscription')}</h6>
            {currentSubscription ? (
              <>
                {currentSubscription.isActive && currentSubscription.status === 'cancel_at_period_end' && (
                  <div className="alert alert-warning mb-3" style={{ fontSize: '0.85rem' }}>
                    {t('user.subscriptionCancelledAtPeriodEnd', { endDate: currentSubscription.endDate })}
                  </div>
                )}
                <h4 className="mb-3 fw-normal text-muted">
                  {currentSubscription.package.name}
                </h4>
                <p className="mb-1">
                  <strong>{t('user.startDate')}:</strong> {currentSubscription.startDate}
                </p>
                <p className="mb-1">
                  <strong>{t('user.endDate')}:</strong> {currentSubscription.endDate}
                </p>
                <p className="mb-3">
                  <strong>{t('user.duration')}:</strong> {t(`enums.subscriptionDuration.${currentSubscription.duration}`)}
                </p>
                <div className="d-grid gap-2">
                  {currentSubscription.status === 'cancel_at_period_end' ? (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => setPendingAction('renewSubscription')}
                    >
                      {t('user.renewSubscription')}
                    </Button>
                  ) : (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setPendingAction('cancelAtPeriodEnd')}
                    >
                      {t('user.cancelAtPeriodEnd')}
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setPendingAction('cancelImmediately')}
                  >
                    {t('user.cancelImmediately')}
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-muted mb-0">{t('user.noCurrentSubscription')}</p>
            )}
            <div className="d-grid mt-2">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="cleanup-tooltip">
                    {t('user.cleanupTooltip')}
                  </Tooltip>
                }
              >
                <Button
                  className="w-100"
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setPendingAction('cleanup')}
                >
                  {t('user.cancelAndClean')}
                </Button>
              </OverlayTrigger>
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Change Package Button */}
      {currentSubscription && (
        <Col sm={12} className="text-end">
          {/* <Button variant="primary" onClick={() => setShowModal(true)}>
            {t('user.assignPackage')}
          </Button> */}
          <Button variant="primary" className="ms-2" onClick={() => setShowChangePackageModal(true)}>
            {t('user.changePackage')}
          </Button>
        </Col>
      )}

      {/* Subscription History */}
      <Col sm={12} className="mt-4">
        <h4>{t('user.subscriptionHistory')}</h4>
        {subscriptionHistory.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>{t('user.packageName')}</th>
                <th>{t('user.startDate')}</th>
                <th>{t('user.endDate')}</th>
                <th>{t('user.duration')}</th>
                <th>{t('user.isActive')}</th>
              </tr>
            </thead>
            <tbody>
              {subscriptionHistory.map((sub, index) => (
                <tr key={index}>
                  <td>{sub.package.name}</td>
                  <td>{sub.startDate}</td>
                  <td>{sub.endDate}</td>
                  <td>{t(`enums.subscriptionDuration.${sub.duration}`)}</td>
                  <td>{sub.isActive ? t('common.yes') : t('common.no')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>{t('user.noSubscriptionHistory')}</p>
        )}
      </Col>

      {/* Change Package Modal */}
      <BasicModal
        show={showChangePackageModal}
        handleClose={() => setShowChangePackageModal(false)}
        handleSave={changePackage}
        title={t('user.changePackage')}
        disabled={!selectedPackage || !duration}
      >
        <div className="mb-3">
          <label className="form-label">{t('user.package')}</label>
          <Select
            id="package"
            classNamePrefix="react-select"
            value={packages.find((pkg) => pkg.value === selectedPackage)}
            onChange={(selectedOption) => setSelectedPackage(selectedOption ? selectedOption.value : null)}
            options={packages}
            isClearable
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('user.duration')}</label>
          <Select
            id="duration"
            classNamePrefix="react-select"
            value={{ label: t(`enums.subscriptionDuration.${duration}`), value: duration }}
            onChange={(selectedOption) => setDuration(selectedOption ? selectedOption.value : 'month')}
            options={subscriptionDuration.map((d: string) => ({
              label: t(`enums.subscriptionDuration.${d}`),
              value: d,
            }))}
          />
        </div>
      </BasicModal>

      {/* Subscription action confirmation modal */}
      <Modal
        show={pendingAction !== null}
        onHide={() => setPendingAction(null)}
        className="fade zoomIn"
        dialogClassName="modal-dialog-centered"
      >
        {pendingAction && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{actionConfig[pendingAction].title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{actionConfig[pendingAction].body}</Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setPendingAction(null)}
                disabled={actionLoading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant={actionConfig[pendingAction].confirmVariant}
                onClick={confirmAction}
                disabled={actionLoading}
              >
                {actionConfig[pendingAction].confirmLabel}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </Row>
  );
};

export default SubscriptionsTab;